import { Button, Select, MenuItem, FormControl, InputLabel, Menu } from "@mui/material";
import Papa from 'papaparse';
import {useState} from "react";

const toCSV = (rows, languageLabel) => {
  const header = ["key", "English", languageLabel];
  const csvRows =[header.join(",")];

  rows.forEach((row) => {
    csvRows.push([row.key, row.value, row[languageLabel] || ""].map(String).join(","));
  });

  return csvRows.join("\n");
};

const buildExportData = (languageItems = [], translationItems =[], languageLabel) => {
  return languageItems
    .filter((langItem) => !langItem.deleteMe)
    .map((langItem) => {
      const translationItem = translationItems?.find(
        (t) => t.languageItem === langItem.id && !t.deleteMe
      );

      return {
        key: langItem.key,
        value: langItem.value,
        [languageLabel]: translationItem ? translationItem.value : "",
      };
    });  
};

export const ExportButton = ({ 
  updatedLanguageItems, 
  updatedLanguageTranslationItems, 
  applicationName, 
  languageLabel
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = (selectedFormat) => {
    downloadData(selectedFormat);
    setAnchorEl(null);
  };
  
  const downloadData = (selectedFormat) => {
    const rows = buildExportData(
      updatedLanguageItems, 
      updatedLanguageTranslationItems, 
      languageLabel
    );

    const dateString = new Date()
      .toLocaleDateString()
      .split("/")
      .reverse()
      .join("");
    
    let blob, fileExt;

    if (selectedFormat === "csv") {
      blob = new Blob([toCSV(rows, languageLabel)], {type: "text/csv"});
      fileExt = "csv";
    } else {
      const jsonData = {
        english: {},
        [languageLabel]: {},
      };

      rows.forEach((row) => {
        jsonData.english[row.key] = row.value;
        jsonData[languageLabel][row.key] = row[languageLabel] || "";
      });      

      blob = new Blob([JSON.stringify(jsonData, null, 4)], {
        type: "application/json",
    });
    fileExt = "json";
  } 

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${applicationName}.${languageLabel}.${dateString}.${fileExt}`;
  link.click();
};

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        className="button languageDisplay_saveBtns"
        onClick={handleClick}
        >
        Download (JSON/CSV)
      </Button> 
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleMenuClick("json")}>
        JSON
        </MenuItem>
        <MenuItem onClick={() => handleMenuClick("csv")}>
        CSV
        </MenuItem>
      </Menu>
    </> 
  );
};

export const ImportButton = ({ 
  updatedLanguageItems,
  updatedLanguageTranslationItems, 
  onAddLanguageTranslationItem,
  languageCode, 
  languageLabel, 
  uploadEl, 
  ULTIEl, 
  setLangaugeTranslationItems, 
  onAddLanguageItem,
  setLanguageItems,
  selectedApp
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = (selectedFormat) => {
    uploadData(selectedFormat);
    setAnchorEl(null);
  }

  const uploadData = (selectedFormat) => {
    if (!uploadEl.files[0]) {
      alert("Please choose a file");
      return;
    }
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      async (event) => {
        try {
          let englishPart = {};
          let translationPart = {};

          if (selectedFormat === "csv") {
            //CSV import handling
            const results = Papa.parse(event.target.result, {
              header: true,
              skipEmptyLines: true,
              transformHeader: (h) => h.trim().toLowerCase(),
            });

            console.log("Parsed CSV:", results);
             
            results.data.forEach((row) => {
              const key = row["key"]?.trim();
              if (!key) return;

              if (row.english) englishPart[key] = row.english.trim();
              if (row[languageLabel.toLowerCase()]) translationPart[key] = row[languageLabel.toLowerCase()].trim();
            });
          } else {
            //JSON import handling
            const parsed = JSON.parse(event.target.result);
            console.log('JSON parsed:', parsed);

            englishPart = parsed.english || {};
            translationPart = parsed[languageLabel] || {};
          }

          //apply englush updates
          console.log("English part parsed:", englishPart);
          console.log("Translation part parsed:", translationPart)

          //make local copies to work with 
          let localUpdatedLanguageItems = [...updatedLanguageItems];
          let localUpdatedLanguageTranslationItems = [...updatedLanguageTranslationItems];

          for (const [key, enVal] of Object.entries(englishPart)) {
              let matchingItemIndex = localUpdatedLanguageItems.findIndex((item) => item.key === key);
              let matchingItem;

              if (matchingItemIndex === -1) {
                const newItem = {
                  application: selectedApp,
                  id: null,
                  key,
                  value: enVal,
                  lastUpdated: new Date().toISOString(),
                };

                //wait for backend response that includes ID
                const savedItem = await onAddLanguageItem(newItem);  
                if (!savedItem || !savedItem.id) {
                  console.error("Failed to add language item:", newItem);
                  continue;
                }

                localUpdatedLanguageItems.push(savedItem);
                matchingItem = savedItem; 
            } else {
                matchingItem = localUpdatedLanguageItems[matchingItemIndex];

                //updates value if different
                if (enVal && enVal !== matchingItem.value) {
                  await setLanguageItems(matchingItemIndex, enVal, "value");
                  matchingItem.value = enVal;
              }
            }
          
            const translationVal = translationPart[key];
            if (!translationVal || !matchingItem?.id) continue;
              
            let matchingTranslationIndex = localUpdatedLanguageTranslationItems.findIndex(
              (t) => t.languageItem === matchingItem.id
            );

            if (matchingTranslationIndex === -1) {
              if (!onAddLanguageTranslationItem) {
                console.error("onAddLAnguageTranslationItem must be provided by parent")
                continue;
              }

              const newTransItem = {
                  id: null,
                  value: translationVal,
                  lastUpdated: new Date().toISOString(),
                  languageItem: matchingItem.id,
                  languageCode,
                  hasUpdated: true
                };

                const savedTranslation = await onAddLanguageTranslationItem(newTransItem);

                if (savedTranslation && savedTranslation.id) {
                  localUpdatedLanguageTranslationItems.push(savedTranslation);
                }
            } else {
             if (matchingTranslationIndex !== -1) {
              let existingTrans = localUpdatedLanguageTranslationItems[matchingTranslationIndex];

              if (existingTrans && existingTrans.value !== translationVal) {
                await setLangaugeTranslationItems(
                  existingTrans.id,
                  translationVal,
                  "value",
                  matchingItem.id
                );

                existingTrans.value = translationVal;
                existingTrans.hasUpdated = true;
              }
             } else {
              console.warn(
                `No translation entry found for key "${key}" (languageItem ID ${matchingItem.id})`
              )
             }
          }
        }
          ULTIEl.forceUpdate();
        } catch (e) {
          console.warn(e)
          alert(`Failed to parse ${selectedFormat.toUpperCase()}!`);
        }
      },
    false
  );

  reader.readAsText(uploadEl.files[0]);
};

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        className="button languageDisplay_saveBtns"
        onClick={handleClick}
      >
        Import (JSON/CSV)
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleMenuClick("json")}>
          JSON
        </MenuItem>
        <MenuItem onClick={() => handleMenuClick("csv")}>
          CSV
        </MenuItem>
      </Menu>    
    </>
  );
};
